import { ensure, required, RuleError, type Tournament } from "./model";
import { elimination, makeMatch, seedOrder } from "./draw";
import { rank, type Standing, type StandingTie } from "./ranking";
export type { Standing, StandingTie } from "./ranking";

export function standings(
  t: Tournament,
  poolId?: string,
  ties?: StandingTie[],
): Standing[] {
  const teams = poolId
    ? required(t.pools.find((p) => p.id === poolId)).teamIds
    : t.teams.map((team) => team.id);
  const rows = teams.map((teamId) => ({
    teamId,
    wins: 0,
    played: 0,
    sets: 0,
    setsLost: 0,
    points: 0,
    pointsLost: 0,
    opponentsWins: 0,
    place: 0,
    tied: false,
  }));
  const byId = new Map(rows.map((row) => [row.teamId, row]));
  const matches = t.matches.filter(
    (m) =>
      m.status === "complete" &&
      (poolId ? m.poolId === poolId : m.stage === "swiss"),
  );
  for (const m of matches) {
    const winner = m.winnerId && byId.get(m.winnerId);
    if (winner) winner.wins++;
    if (m.resolution !== "played" && m.resolution !== "retirement") continue;
    m.teams.forEach((id, side) => {
      const row = id && byId.get(id);
      if (!row) return;
      row.played++;
      for (const set of m.sets) {
        row.points += required(set[side]);
        row.pointsLost += required(set[1 - side]);
        if (required(set[side]) > required(set[1 - side])) row.sets++;
        if (required(set[side]) < required(set[1 - side])) row.setsLost++;
      }
    });
  }
  for (const m of matches) {
    const a = m.teams[0] && byId.get(m.teams[0]);
    const b = m.teams[1] && byId.get(m.teams[1]);
    if (a && b) {
      a.opponentsWins += b.wins;
      b.opponentsWins += a.wins;
    }
  }
  return rank(t, rows, {
    scope: poolId ? `pool:${poolId}` : "swiss",
    label: poolId ? `Pool ${poolId}` : "Swiss standings",
    compare: (a, b) =>
      b.wins - a.wins || (poolId ? 0 : b.opponentsWins - a.opponentsWins),
    matches,
    fallback: (a, b) => b.sets - a.sets || b.points - a.points,
    ties,
  });
}
export function kingStandings(t: Tournament, ties?: StandingTie[]): Standing[] {
  return rank(
    t,
    t.teams.map((team) => ({
      teamId: team.id,
      wins: 0,
      sets: 0,
      setsLost: 0,
      pointsLost: 0,
      opponentsWins: 0,
      place: 0,
      tied: false,
      played: t.heats.filter(
        (h) => h.status === "complete" && h.teamIds.includes(team.id),
      ).length,
      points: t.heats.reduce((sum, h) => sum + (h.totals?.[team.id] ?? 0), 0),
    })),
    {
      scope: "king",
      label: "Heat totals",
      compare: (a, b) => b.points - a.points,
      ties,
    },
  );
}
export function poolQualifiers(t: Tournament, ties?: StandingTie[]): string[] {
  const total = t.config.qualifiers;
  ensure(
    total >= 2 && total <= t.teams.filter((team) => !team.withdrawn).length,
    "Choose a valid number of qualifying teams.",
  );
  const base = Math.floor(total / t.pools.length);
  const perPool = t.pools.map((p) =>
    standings(t, p.id, ties).filter(
      (row) => !required(t.teams.find((v) => v.id === row.teamId)).withdrawn,
    ),
  );
  ensure(
    perPool.every((rows) => rows.length >= base),
    "A pool has too few eligible teams. Adjust qualification or resolve withdrawals.",
  );
  ensure(
    perPool.every((rows) => rows.every((row) => !row.tied)),
    "Resolve the tied pool standings before generating the playoff.",
    "tie",
  );
  const selected = perPool.flatMap((rows) => rows.slice(0, base));
  const extra = total - selected.length;
  const candidates = perPool.flatMap((rows) =>
    rows[base] ? [rows[base]!] : [],
  );
  const all = perPool.flat();
  const unequal = new Set(all.map((row) => row.played)).size > 1;
  const compare = (a: Standing, b: Standing) => {
    const ac = unequal ? Math.max(1, a.played) : 1;
    const bc = unequal ? Math.max(1, b.played) : 1;
    return b.sets / bc - a.sets / ac || b.points / bc - a.points / ac;
  };
  let rankedExtras: Standing[] = [];
  if (extra) {
    const remainingTies: StandingTie[] = [];
    const ranked = rank(t, candidates, {
      scope: "qualifiers",
      label: "Remaining qualifying places",
      compare,
      ties: remainingTies,
    });
    ties?.push(...remainingTies.filter((tie) => tie.place <= extra));
    ensure(
      !ranked.slice(0, extra).some((row) => row.tied),
      "Resolve the cross-pool tie before choosing the remaining qualifiers.",
      "tie",
    );
    rankedExtras = ranked.slice(0, extra);
    selected.push(...rankedExtras);
  }
  const poolPosition = (row: Standing) =>
    required(
      perPool.find((rows) => rows.some((r) => r.teamId === row.teamId)),
    ).findIndex((r) => r.teamId === row.teamId);
  const seeded = [...new Set(selected.map(poolPosition))]
    .sort((a, b) => a - b)
    .flatMap((position) =>
      position === base
        ? rankedExtras
        : rank(
            t,
            selected.filter((row) => poolPosition(row) === position),
            {
              scope: `playoff:${position + 1}`,
              label: "Playoff seeding",
              placeOffset: selected.filter(
                (row) => poolPosition(row) < position,
              ).length,
              compare,
              ties,
            },
          ),
    );
  ensure(
    !seeded.some((row) => row.tied),
    "Resolve the cross-pool tie before seeding the playoff.",
    "tie",
  );
  return seeded.map((row) => row.teamId);
}

export function unresolvedTies(t: Tournament): StandingTie[] {
  if (t.status !== "running" || t.playoffGenerated) return [];
  const ties: StandingTie[] = [];
  if (t.config.mode === "pools") {
    const complete = t.pools.filter((pool) =>
      t.matches
        .filter((m) => m.poolId === pool.id)
        .every((m) => m.status === "complete"),
    );
    if (complete.length !== t.pools.length) {
      complete.forEach((pool) => standings(t, pool.id, ties));
    } else {
      try {
        poolQualifiers(t, ties);
      } catch (error) {
        if (!(error instanceof RuleError)) throw error;
      }
    }
  } else if (
    t.config.mode === "king" &&
    t.heats.length > 0 &&
    t.heats.every((h) => h.status === "complete") &&
    Math.max(...t.heats.map((h) => h.round)) === t.config.heatCount
  ) {
    kingStandings(t, ties);
  } else if (
    t.config.mode === "swiss" &&
    t.matches.length > 0 &&
    t.matches.every((m) => m.status === "complete") &&
    Math.max(...t.matches.map((m) => m.round)) === t.config.swissRounds
  ) {
    standings(t, undefined, ties);
  }
  return ties;
}
export function startPlayoff(t: Tournament): void {
  ensure(!t.playoffGenerated, "The playoff already exists.");
  let ranked: string[];
  if (t.config.mode === "pools") {
    ensure(
      t.matches.every((m) => m.status === "complete"),
      "Finish all pool matches first.",
    );
    ranked = poolQualifiers(t);
  } else {
    if (t.config.mode === "king")
      ensure(
        t.heats.length > 0 &&
          t.heats.every((h) => h.status === "complete") &&
          Math.max(...t.heats.map((h) => h.round)) === t.config.heatCount,
        "Finish all heats first.",
      );
    else
      ensure(
        t.matches.every((m) => m.status === "complete") &&
          Math.max(...t.matches.map((m) => m.round)) === t.config.swissRounds,
        "Finish all Swiss rounds first.",
      );
    const rows = (
      t.config.mode === "king" ? kingStandings(t) : standings(t)
    ).filter(
      (row) => !required(t.teams.find((v) => v.id === row.teamId)).withdrawn,
    );
    ensure(
      !rows.some((row) => row.tied),
      "Resolve the tied standings before proceeding.",
      "tie",
    );
    if (t.config.mode === "swiss" && t.config.qualifiers === 0) {
      t.status = "finished";
      return;
    }
    ranked = rows.slice(0, t.config.qualifiers).map((row) => row.teamId);
  }
  elimination(
    t,
    t.config.mode === "pools" ? avoidPoolRematches(t, ranked) : ranked,
  );
  t.playoffGenerated = true;
}

export function avoidPoolRematches(t: Tournament, ranked: string[]): string[] {
  const size = 2 ** Math.ceil(Math.log2(ranked.length));
  const order = seedOrder(size);
  const poolOf = new Map(
    t.pools.flatMap((p) => p.teamIds.map((id) => [id, p.id] as const)),
  );
  const lower = ranked.slice(size / 2);
  const slots = lower.map((_, i) => {
    const seed = size / 2 + i + 1;
    const position = order.indexOf(seed);
    const opponentSeed = required(order[position ^ 1]);
    return poolOf.get(required(ranked[opponentSeed - 1]));
  });
  const assigned = new Map<string, number>();
  const fill = (slot: number, visited: Set<string>): boolean => {
    const candidates = lower.toSorted(
      (a, b) =>
        Math.abs(lower.indexOf(a) - slot) - Math.abs(lower.indexOf(b) - slot),
    );
    for (const id of candidates) {
      if (visited.has(id) || poolOf.get(id) === slots[slot]) continue;
      visited.add(id);
      const previous = assigned.get(id);
      if (previous === undefined || fill(previous, visited)) {
        assigned.set(id, slot);
        return true;
      }
    }
    return false;
  };
  slots.forEach((_, i) => fill(i, new Set()));
  const result = [...ranked];
  const unused = lower.filter((id) => !assigned.has(id));
  for (const [id, slot] of assigned) result[size / 2 + slot] = id;
  slots.forEach((_, slot) => {
    if (![...assigned.values()].includes(slot))
      result[size / 2 + slot] = required(unused.shift());
  });
  return result;
}

export function swissRound(t: Tournament): void {
  ensure(
    t.matches.every((m) => m.status === "complete"),
    "Finish the current round first.",
  );
  const round = Math.max(0, ...t.matches.map((m) => m.round)) + 1;
  ensure(round <= t.config.swissRounds, "All Swiss rounds are complete.");
  const rows = standings(t).filter(
    (row) => !required(t.teams.find((v) => v.id === row.teamId)).withdrawn,
  );
  const previous = new Set(
    t.matches
      .filter((m) => m.teams.every(Boolean))
      .map((m) => [...m.teams].sort().join("|")),
  );
  if (rows.length % 2) {
    const byes = new Set(
      t.matches.filter((m) => m.resolution === "bye").map((m) => m.winnerId),
    );
    const eligible =
      [...rows].reverse().find((row) => !byes.has(row.teamId)) ?? rows.at(-1)!;
    rows.splice(rows.indexOf(eligible), 1);
    makeMatch(
      t,
      [{ teamId: eligible.teamId }, { bye: true }],
      "swiss",
      round,
      round,
    );
  }
  let attempts = 0;
  const search = (remaining: Standing[]): Array<[string, string]> | null => {
    if (!remaining.length) return [];
    if (++attempts > 50000) return null;
    const first = required(remaining[0]);
    const candidates = remaining
      .slice(1)
      .filter(
        (row) => !previous.has([first.teamId, row.teamId].sort().join("|")),
      )
      .sort(
        (a, b) => Math.abs(first.wins - a.wins) - Math.abs(first.wins - b.wins),
      );
    for (const other of candidates) {
      const tail = search(
        remaining.filter((row) => row !== first && row !== other),
      );
      if (tail) return [[first.teamId, other.teamId], ...tail];
    }
    return null;
  };
  const pairs = search(rows);
  ensure(
    pairs,
    "No draw without rematches was found. Reduce the Swiss round count or use the organizer's manual pairings.",
    "pairing",
  );
  for (const [a, b] of pairs)
    makeMatch(t, [{ teamId: a }, { teamId: b }], "swiss", round, round);
}

export function kingRound(t: Tournament): void {
  ensure(
    t.heats.every((h) => h.status === "complete"),
    "Finish the current heat round first.",
  );
  const round = Math.max(0, ...t.heats.map((h) => h.round)) + 1;
  ensure(round <= t.config.heatCount, "All heats are complete.");
  const teams = t.teams
    .filter((team) => !team.withdrawn)
    .toSorted((a, b) => a.seed - b.seed);
  const count = Math.ceil(teams.length / 6);
  ensure(
    teams.length >= 2 &&
      (t.withdrawals.length > 0 || Math.floor(teams.length / count) >= 4),
    "Too few teams remain to generate balanced heats. Review the remaining rounds.",
  );
  const groups: string[][] = Array.from({ length: count }, () => []);
  const sizes = groups.map(
    (_, i) =>
      Math.floor(teams.length / count) + (i < teams.length % count ? 1 : 0),
  );
  for (const team of teams) {
    const candidates = groups
      .map((group, i) => ({ i, group }))
      .filter(({ group, i }) => group.length < required(sizes[i]));
    const cost = (group: string[], i: number) =>
      group.length * 1000 +
      group.reduce(
        (sum, other) =>
          sum +
          t.heats.filter(
            (h) => h.teamIds.includes(team.id) && h.teamIds.includes(other),
          ).length *
            100,
        0,
      ) +
      ((i + round - 1) % count);
    candidates.sort((a, b) => cost(a.group, a.i) - cost(b.group, b.i));
    required(candidates[0]).group.push(team.id);
  }
  for (const [i, teamIds] of groups.entries()) {
    t.heats.push({
      id: `h${t.heats.length + 1}`,
      round,
      teamIds,
      courtId: required(t.courts[i % t.courts.length]).id,
      status: "ready",
      revision: 0,
    });
  }
}
